import { callExternalApi, CallResult } from '../callExternalApi';
import {
  AdviseEngagement,
  EngagementListItem,
  NewEngagementRequest,
  EngagementConfiguration,
  DiscoveryAnswer,
  DiscoveryFormSection,
  MatrixCategory,
  MatrixCriterion,
  MatrixCriterionValue,
  MatrixFeature,
  NewMatrixCriterion
} from '../../types/advise';
import { withAdviseAccountIds } from './helpers';

export {
  createAdviseCurrentSupplier,
  deleteAdviseCurrentSupplier,
  deleteAdviseSupplierInvoice,
  fetchAdviseInvoiceFile,
  updateAdviseCurrentSupplier,
  updateAdviseCurrentSuppliersSkip,
  updateAdviseInvoiceLineItems,
  updateAdviseSupplierLineItems,
  updateAdviseSupplierSelection,
  uploadAdviseSupplierInvoice
} from './EngagementSuppliers';

export const fetchAdviseEngagements = async (): Promise<CallResult<AdviseEngagement[]>> =>
  withAdviseAccountIds('engagements', (accountIds) =>
    callExternalApi<AdviseEngagement[]>({
      url: `api/advise/engagements?account_ids=${accountIds.join(',')}`,
      method: 'GET',
      headers: { 'content-type': 'application/json' }
    })
  );

export const fetchMyEngagements = async (): Promise<CallResult<EngagementListItem[]>> => {
  return callExternalApi<EngagementListItem[]>({
    url: 'api/advise/engagements',
    method: 'GET',
    headers: { 'content-type': 'application/json' }
  });
};

export const fetchTeamEngagements = async (): Promise<CallResult<EngagementListItem[]>> => {
  return callExternalApi<EngagementListItem[]>({
    url: 'api/advise/engagements?scope=team',
    method: 'GET',
    headers: { 'content-type': 'application/json' }
  });
};

export const fetchAdviseEngagementById = async (id: string): Promise<CallResult<AdviseEngagement>> => {
  return callExternalApi<AdviseEngagement>({
    url: `api/advise/engagements/${id}`,
    method: 'GET',
    headers: { 'content-type': 'application/json' }
  });
};

export const createEngagement = async (request: NewEngagementRequest): Promise<CallResult<AdviseEngagement>> => {
  return callExternalApi<AdviseEngagement>({
    url: 'api/advise/engagements',
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    data: request
  });
};

export const updateAdviseEngagement = async (
  id: string,
  engagement: Partial<AdviseEngagement>
): Promise<CallResult<AdviseEngagement>> => {
  return callExternalApi<AdviseEngagement>({
    url: `api/advise/engagements/${id}`,
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    data: engagement
  });
};

export const deleteAdviseEngagement = async (id: string): Promise<CallResult<void>> => {
  return callExternalApi<void>({
    url: `api/advise/engagements/${id}`,
    method: 'DELETE',
    headers: { 'content-type': 'application/json' }
  });
};

export const updateAdviseRoi = async (
  id: string,
  roiAnalysis: Record<string, unknown>
): Promise<CallResult<AdviseEngagement>> => {
  return callExternalApi<AdviseEngagement>({
    url: `api/advise/engagements/${id}/roi`,
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    data: { roi_analysis: roiAnalysis }
  });
};

export const fetchAdviseDiscovery = async (id: string): Promise<CallResult<{ sections: DiscoveryFormSection[] }>> => {
  return callExternalApi<{ sections: DiscoveryFormSection[] }>({
    url: `api/advise/engagements/${id}/discovery`,
    method: 'GET',
    headers: { 'content-type': 'application/json' }
  });
};

export const updateAdviseDiscoveryAnswer = async (
  id: string,
  questionId: string,
  value: DiscoveryAnswer
): Promise<CallResult<{ question_id: string; value: DiscoveryAnswer }>> => {
  return callExternalApi<{ question_id: string; value: DiscoveryAnswer }>({
    url: `api/advise/engagements/${id}/discovery/questions/${questionId}`,
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    data: { value }
  });
};

export const fetchAdviseFunctionalityMatrix = async (
  id: string
): Promise<CallResult<{ criteria: MatrixCriterion[] }>> => {
  return callExternalApi<{ criteria: MatrixCriterion[] }>({
    url: `api/advise/engagements/${id}/functionality_matrix`,
    method: 'GET',
    headers: { 'content-type': 'application/json' }
  });
};

export const updateAdviseMatrixCriterion = async (
  id: string,
  criterionId: string,
  value: MatrixCriterionValue
): Promise<CallResult<{ criterion_id: string; value: MatrixCriterionValue }>> => {
  return callExternalApi<{ criterion_id: string; value: MatrixCriterionValue }>({
    url: `api/advise/engagements/${id}/functionality_matrix/criteria/${criterionId}`,
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    data: { value }
  });
};

export const createAdviseMatrixCriterion = async (
  id: string,
  criterion: NewMatrixCriterion
): Promise<CallResult<{ criterion: MatrixCriterion }>> => {
  return callExternalApi<{ criterion: MatrixCriterion }>({
    url: `api/advise/engagements/${id}/functionality_matrix/criteria`,
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    data: criterion
  });
};

export const createAdviseMatrixCategoryFeature = async (
  id: string,
  categoryId: string,
  label: string
): Promise<CallResult<{ feature: MatrixFeature }>> => {
  return callExternalApi<{ feature: MatrixFeature }>({
    url: `api/advise/engagements/${id}/functionality_matrix/categories/${categoryId}/features`,
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    data: { label }
  });
};

export const createAdviseMatrixCriterionFeature = async (
  id: string,
  criterionId: string,
  label: string
): Promise<CallResult<{ category: MatrixCategory }>> => {
  return callExternalApi<{ category: MatrixCategory }>({
    url: `api/advise/engagements/${id}/functionality_matrix/criteria/${criterionId}/features`,
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    data: { label }
  });
};

export const deleteAdviseMatrixCriterion = async (
  id: string,
  criterionId: string
): Promise<CallResult<{ criterion_id: string }>> => {
  return callExternalApi<{ criterion_id: string }>({
    url: `api/advise/engagements/${id}/functionality_matrix/criteria/${criterionId}`,
    method: 'DELETE',
    headers: { 'content-type': 'application/json' }
  });
};

export const deleteAdviseMatrixFeature = async (
  id: string,
  featureId: string
): Promise<CallResult<{ feature_id: string; category_removed: boolean }>> => {
  return callExternalApi<{ feature_id: string; category_removed: boolean }>({
    url: `api/advise/engagements/${id}/functionality_matrix/features/${featureId}`,
    method: 'DELETE',
    headers: { 'content-type': 'application/json' }
  });
};

export const updateAdviseMatrixFeature = async (
  id: string,
  featureId: string,
  selected: boolean
): Promise<CallResult<{ feature_id: string; value: { selected: boolean } }>> => {
  return callExternalApi<{ feature_id: string; value: { selected: boolean } }>({
    url: `api/advise/engagements/${id}/functionality_matrix/features/${featureId}`,
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    data: { value: { selected } }
  });
};

export const fetchAdviseEngagementConfiguration = async (id: string): Promise<CallResult<EngagementConfiguration>> => {
  return callExternalApi<EngagementConfiguration>({
    url: `api/advise/engagements/${id}/configuration`,
    method: 'GET',
    headers: { 'content-type': 'application/json' }
  });
};

export const updateAdviseEngagementConfiguration = async (
  id: string,
  steps: Record<string, boolean>
): Promise<CallResult<EngagementConfiguration>> => {
  return callExternalApi<EngagementConfiguration>({
    url: `api/advise/engagements/${id}/configuration`,
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    data: { steps }
  });
};

export const addAdviseShare = async (id: string, emails: string[]): Promise<CallResult<AdviseEngagement>> => {
  return callExternalApi<AdviseEngagement>({
    url: `api/advise/engagements/${id}/shares`,
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    data: { emails }
  });
};

export const removeAdviseShare = async (id: string, email: string): Promise<CallResult<AdviseEngagement>> => {
  return callExternalApi<AdviseEngagement>({
    url: `api/advise/engagements/${id}/shares`,
    method: 'DELETE',
    headers: { 'content-type': 'application/json' },
    data: { email }
  });
};

export const updateAdviseActionPlan = async (
  id: string,
  mutualActionPlan: { milestones: unknown[] }
): Promise<CallResult<AdviseEngagement>> => {
  return callExternalApi<AdviseEngagement>({
    url: `api/advise/engagements/${id}/action_plan`,
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    data: { mutual_action_plan: mutualActionPlan }
  });
};

export const submitAdviseDealRegistration = async (
  id: string,
  dealData: Record<string, unknown>
): Promise<CallResult<{ success: boolean }>> => {
  return callExternalApi<{ success: boolean }>({
    url: `api/advise/engagements/${id}/deal_registrations`,
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    data: dealData
  });
};
